import { Stack } from "@mui/material";
import { Switch } from "features/shared/components/Switch";
import { useTranslation } from "react-i18next";

import type { DayNightFlushingConfig } from "../model/transientScenario";

import { ConfigurationSwitchRow } from "./ConfigurationSwitchRow";
import { FlushingSettingsRow } from "./FlushingSettingsRow";
import { ScenarioDayNightFlushingHeader } from "./ScenarioDayNightFlushingHeader";
import { ScenarioDayNightFlushingHoursCell } from "./ScenarioDayNightFlushingHoursCell";
import { ScenarioDayNightFlushingNumericCell } from "./ScenarioDayNightFlushingNumericCell";

type NumericFlushingKey = {
  [K in keyof DayNightFlushingConfig]: DayNightFlushingConfig[K] extends number ? K : never;
}[keyof DayNightFlushingConfig];

type ScenarioDayNightFlushingSettingsProps = {
  dayNightFlushing: DayNightFlushingConfig | null;
  onDayNightFlushingChange: <K extends keyof DayNightFlushingConfig>(key: K, value: DayNightFlushingConfig[K]) => void;
};

const NUMERIC_ROWS: Array<{ dayKey: NumericFlushingKey; labelKey: string; nightKey: NumericFlushingKey }> = [
  {
    dayKey: "flushing_t_exhaust_day_threshold",
    labelKey: "exhaustTempThreshold",
    nightKey: "flushing_t_exhaust_night_threshold",
  },
  { dayKey: "flush_temp_lower_day_limit", labelKey: "lowerTempLimit", nightKey: "flush_temp_lower_night_limit" },
  { dayKey: "flush_temp_upper_day_limit", labelKey: "upperTempLimit", nightKey: "flush_temp_upper_night_limit" },
];

export const ScenarioDayNightFlushingSettings = ({
  dayNightFlushing,
  onDayNightFlushingChange,
}: ScenarioDayNightFlushingSettingsProps) => {
  const { t } = useTranslation();
  const label = (key: string) => t(`Execution.transient.scenario.dayNightFlushing.${key}`);

  if (!dayNightFlushing) return null;

  return (
    <Stack className=" w-full flex-col gap-3 rounded-ds-0-625x bg-lds-surface-muted p-ds-1-5x border-ds-soft">
      <ScenarioDayNightFlushingHeader />
      <FlushingSettingsRow
        dayControl={
          <Switch
            checked={dayNightFlushing.enable_day_flushing}
            onChange={(value) => onDayNightFlushingChange("enable_day_flushing", value)}
            size="small"
          />
        }
        label={label("enableRow")}
        nightControl={
          <Switch
            checked={dayNightFlushing.enable_night_flushing}
            onChange={(value) => onDayNightFlushingChange("enable_night_flushing", value)}
            size="small"
          />
        }
      />
      {NUMERIC_ROWS.map((row) => (
        <FlushingSettingsRow
          dayControl={
            <ScenarioDayNightFlushingNumericCell
              onChange={(value) => onDayNightFlushingChange(row.dayKey, value)}
              value={dayNightFlushing[row.dayKey]}
            />
          }
          key={row.labelKey}
          label={label(row.labelKey)}
          nightControl={
            <ScenarioDayNightFlushingNumericCell
              onChange={(value) => onDayNightFlushingChange(row.nightKey, value)}
              value={dayNightFlushing[row.nightKey]}
            />
          }
        />
      ))}
      <FlushingSettingsRow
        dayControl={
          <ScenarioDayNightFlushingHoursCell
            onChange={(hours) => onDayNightFlushingChange("flushing_day_hours", hours)}
            selectedHours={dayNightFlushing.flushing_day_hours}
          />
        }
        label={label("activeHours")}
        nightControl={
          <ScenarioDayNightFlushingHoursCell
            onChange={(hours) => onDayNightFlushingChange("flushing_night_hours", hours)}
            selectedHours={dayNightFlushing.flushing_night_hours}
          />
        }
      />
      <ConfigurationSwitchRow
        checked={dayNightFlushing.bypass}
        label={label("bypass")}
        onChange={(value) => onDayNightFlushingChange("bypass", value)}
      />
    </Stack>
  );
};
